import GearTrainsImg from '../../Images/GearTrainsImg.jpg'



// ── Shared Styles (earthy orange palette) ───────────────────────────
const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid #7a2e00', fontFamily:"'Georgia', serif", color:'#1a0e06', lineHeight:'1.7', background:'#fdf6ee' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c45a10', paddingBottom:'8px', marginBottom:'24px', color:'#7a2e00' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#a04010', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c45a10', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#fdf0e4', border:'2px solid #c45a10', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7a2e00', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c45a10' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#a04010', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf4ec' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf6ee' },
  tdL: { fontWeight:'600', color:'#7a2e00', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fdf4ec', border:'1px solid #e8b07a', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7a2e00', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#7a4a28', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Detail</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Helper: draw a gear with N teeth at (cx,cy) radius r ─────────────
function Gear({ cx, cy, r, teeth, fill, stroke, strokeWidth=2, label, labelColor='#fff', sublabel, toothH }) {
  const th = toothH || r * 0.18;
  const pts = [];
  for (let i = 0; i < teeth; i++) {
    const a0 = (2 * Math.PI * i) / teeth;
    const aW = Math.PI * 0.6 / teeth; // half tooth width angle
    const ro = r + th;
    // root → flank → tip → flank → root
    pts.push([r  * Math.cos(a0 - aW*1.4), r  * Math.sin(a0 - aW*1.4)]);
    pts.push([ro * Math.cos(a0 - aW*0.5), ro * Math.sin(a0 - aW*0.5)]);
    pts.push([ro * Math.cos(a0 + aW*0.5), ro * Math.sin(a0 + aW*0.5)]);
    pts.push([r  * Math.cos(a0 + aW*1.4), r  * Math.sin(a0 + aW*1.4)]);
  }
  const d = 'M ' + pts.map(p => `${(cx+p[0]).toFixed(1)},${(cy+p[1]).toFixed(1)}`).join(' L ') + ' Z';
  const spokes = [0,60,120,180,240,300];
  return (
    <g>
      <path d={d} fill={fill} stroke={stroke} strokeWidth={strokeWidth}/>
      <circle cx={cx} cy={cy} r={r*0.55} fill={fill} stroke={stroke} strokeWidth={strokeWidth*0.7} opacity="0.6"/>
      {spokes.map((ang, i) => {
        const rad = ang * Math.PI / 180;
        return (
          <line key={i}
            x1={cx + r*0.18*Math.cos(rad)} y1={cy + r*0.18*Math.sin(rad)}
            x2={cx + r*0.52*Math.cos(rad)} y2={cy + r*0.52*Math.sin(rad)}
            stroke={stroke} strokeWidth={strokeWidth*0.6} opacity="0.5"/>
        );
      })}
      <circle cx={cx} cy={cy} r={r*0.14} fill="#fdf4ec" stroke={stroke} strokeWidth={strokeWidth*0.7}/>
      {label && <text x={cx} y={cy+4} textAnchor="middle" fontSize={r*0.28} fontWeight="700" fill={labelColor}>{label}</text>}
      {sublabel && <text x={cx} y={cy+r+th+14} textAnchor="middle" fontSize="9" fill="#7a4a28">{sublabel}</text>}
    </g>
  );
}

// ── Diagram 1: Simple Gear Pair — anatomy & mesh ─────────────────────
function DiagramSimplePair() {
  const W = 900, H = 320;
  const cx1 = 230, cy = 155, r1 = 80, t1 = 16;
  const th1 = r1 * 0.16;
  const cx2 = cx1 + r1 + 70 + th1*2, r2 = 70, t2 = 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Simple Gear Pair — Anatomy, Mesh Point & Direction of Rotation</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arrowG1" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* Gear 1 — driver */}
        <Gear cx={cx1} cy={cy} r={r1} teeth={t1} fill="#c45a10" stroke="#7a2e00" strokeWidth={2.5} label="1" labelColor="#fdf4ec"/>
        {/* Gear 2 — driven */}
        <Gear cx={cx2} cy={cy} r={r2} teeth={t2} fill="#d4874a" stroke="#7a2e00" strokeWidth={2.5} label="2" labelColor="#fdf4ec"/>

        {/* Shaft lines */}
        {[[cx1,cy+r1+th1*1.2],[cx2,cy+r2+r2*0.16*1.2]].map(([x,y],i)=>(
          <line key={i} x1={x} y1={y} x2={x} y2={y+40} stroke="#7a2e00" strokeWidth="4" strokeDasharray="6,3"/>
        ))}
        {[[cx1,cy+r1+th1*1.2+40],[cx2,cy+r2+r2*0.16*1.2+40]].map(([x,y],i)=>(
          <g key={i}>
            <line x1={x-22} y1={y} x2={x+22} y2={y} stroke="#7a2e00" strokeWidth="3"/>
            {[-18,-9,0,9,18].map((dx,j)=>(
              <line key={j} x1={x+dx} y1={y} x2={x+dx-6} y2={y+10} stroke="#7a2e00" strokeWidth="1.5"/>
            ))}
          </g>
        ))}

        {/* Rotation arrows */}
        <path d={`M ${cx1} ${cy-r1-th1*1.1-12} A ${r1+th1*1.1+12} ${r1+th1*1.1+12} 0 0 1 ${cx1+r1+th1*1.1+12} ${cy}`}
          fill="none" stroke="#c45a10" strokeWidth="2" markerEnd="url(#arrowG1)"/>
        <text x={cx1-20} y={cy-r1-th1*1.1-22} fontSize="10" fontWeight="700" fill="#c45a10">ω₁ (CW)</text>

        <path d={`M ${cx2+r2+r2*0.16*1.1+12} ${cy} A ${r2+r2*0.16*1.1+12} ${r2+r2*0.16*1.1+12} 0 0 1 ${cx2} ${cy-r2-r2*0.16*1.1-12}`}
          fill="none" stroke="#a04010" strokeWidth="2" markerEnd="url(#arrowG1)"/>
        <text x={cx2+r2+r2*0.16*1.1+16} y={cy-14} fontSize="10" fontWeight="700" fill="#a04010">ω₂ (CCW)</text>

        {/* Mesh point annotation */}
        <circle cx={(cx1+r1+cx2-r2)/2} cy={cy} r={6} fill="#7a2e00"/>
        <line x1={(cx1+r1+cx2-r2)/2} y1={cy-6} x2={(cx1+r1+cx2-r2)/2} y2={cy-32} stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={(cx1+r1+cx2-r2)/2} y={cy-36} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Pitch Point</text>

        {/* Pitch circles */}
        <circle cx={cx1} cy={cy} r={r1} fill="none" stroke="#e8b07a" strokeWidth="1.5" strokeDasharray="5,3" opacity="0.8"/>
        <circle cx={cx2} cy={cy} r={r2} fill="none" stroke="#e8b07a" strokeWidth="1.5" strokeDasharray="5,3" opacity="0.8"/>
        <text x={cx1-r1-4} y={cy+18} textAnchor="end" fontSize="8" fill="#a04010" fontStyle="italic">pitch circle</text>

        {/* Centre distance arrow */}
        <line x1={cx1} y1={cy+r1+th1*1.2+52} x2={cx2} y2={cy+r1+th1*1.2+52}
          stroke="#a04010" strokeWidth="1.5" markerStart="url(#arrowG1)" markerEnd="url(#arrowG1)"/>
        <rect x={(cx1+cx2)/2-30} y={cy+r1+th1*1.2+44} width={60} height={16} rx={3} fill="#fdf0e4" stroke="#e8b07a"/>
        <text x={(cx1+cx2)/2} y={cy+r1+th1*1.2+55} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">C = r₁ + r₂</text>

        {/* Info panel */}
        <rect x={560} y={20} width={325} height={275} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={722} y={40} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Gear Pair Fundamentals</text>
        <line x1={570} y1={46} x2={876} y2={46} stroke="#e8b07a"/>
        {[
          ['Gear Ratio:', 'i = N₂/N₁ = ω₁/ω₂ = r₂/r₁'],
          ['', '(N = number of teeth)'],
          ['Direction:', 'External gears → opposite rotation'],
          ['', 'Internal gear → same rotation'],
          ['Pitch circle:', 'Imaginary circle where gears roll'],
          ['', 'without slip; r = m·N/2'],
          ['Module (m):', 'm = d / N  (SI standard)'],
          ['', 'Larger m → larger, stronger teeth'],
          ['Diametral pitch:', 'P = N / d  (imperial)  P = 25.4/m'],
          ['Pressure angle:', 'φ = 20° standard (14.5° older)'],
          ['', 'Angle of force at contact point'],
          ['Addendum:', 'Tooth height above pitch circle = m'],
          ['Dedendum:', 'Tooth depth below pitch circle = 1.25m'],
          ['Contact ratio:', '> 1.2 required for smooth meshing'],
        ].map(([lbl, val], i) => (
          <g key={i}>
            <text x={572} y={60+i*16} fontSize="8.5" fontWeight={lbl?'700':'400'} fill="#7a2e00">{lbl}</text>
            <text x={665} y={60+i*16} fontSize="8.5" fontFamily="'Courier New',monospace" fill="#a04010">{val}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>External gear pairs reverse rotation direction · pitch circles are tangent at the pitch point · module must match for gears to mesh</div>
    </div>
  );
}

// ── Diagram 2: Gear Train Types ──────────────────────────────────────
function DiagramGearTypes() {
  const W = 900, H = 310;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Common Gear Types Used in Gear Trains</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* SPUR GEAR — simple 2D representation */}
        <g>
          <text x={90} y={20} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Spur Gear</text>
          <Gear cx={90} cy={100} r={55} teeth={14} fill="#c45a10" stroke="#7a2e00" strokeWidth={2}/>
          <text x={90} y={185} textAnchor="middle" fontSize="9" fill="#a04010">Teeth parallel to axis</text>
          <text x={90} y={197} textAnchor="middle" fontSize="9" fill="#a04010">High efficiency ~98-99%</text>
          <text x={90} y={209} textAnchor="middle" fontSize="9" fill="#a04010">Noisy at high speed</text>
          <text x={90} y={221} textAnchor="middle" fontSize="9" fill="#a04010">Parallel shafts only</text>
        </g>

        {/* HELICAL GEAR — draw with angled lines on teeth */}
        <g transform="translate(220,0)">
          <text x={80} y={20} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Helical Gear</text>
          <Gear cx={80} cy={100} r={52} teeth={14} fill="#d4874a" stroke="#7a2e00" strokeWidth={2}/>
          {/* helix angle lines across face */}
          {[-30,-15,0,15,30].map((dx,i)=>(
            <line key={i} x1={80+dx} y1={54} x2={80+dx+18} y2={148}
              stroke="#7a2e00" strokeWidth="1.2" opacity="0.5" strokeDasharray="3,2"/>
          ))}
          <text x={80} y={185} textAnchor="middle" fontSize="9" fill="#a04010">Teeth angled (15–30°)</text>
          <text x={80} y={197} textAnchor="middle" fontSize="9" fill="#a04010">Quieter, smoother</text>
          <text x={80} y={209} textAnchor="middle" fontSize="9" fill="#a04010">Axial thrust force</text>
          <text x={80} y={221} textAnchor="middle" fontSize="9" fill="#a04010">Parallel or crossed shafts</text>
        </g>

        {/* BEVEL GEAR — side view trapezoid representation */}
        <g transform="translate(430,20)">
          <text x={70} y={0} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Bevel Gear</text>
          {/* Cone body */}
          <polygon points="10,80 130,80 100,30 40,30" fill="#c45a10" stroke="#7a2e00" strokeWidth="2" opacity="0.85"/>
          {/* Teeth on outer edge */}
          {Array.from({length:9},(_,i)=>{
            const x = 10 + i*14;
            return <rect key={i} x={x+2} y={76} width={10} height={8} rx={1} fill="#7a2e00" opacity="0.7"/>
          })}
          {/* Shaft */}
          <line x1={70} y1={30} x2={70} y2={10} stroke="#7a2e00" strokeWidth="4"/>
          <text x={70} y={155} textAnchor="middle" fontSize="9" fill="#a04010">Intersecting shafts (90°)</text>
          <text x={70} y={167} textAnchor="middle" fontSize="9" fill="#a04010">Transmits at angle</text>
          <text x={70} y={179} textAnchor="middle" fontSize="9" fill="#a04010">Straight or spiral teeth</text>
          <text x={70} y={191} textAnchor="middle" fontSize="9" fill="#a04010">Used in differentials</text>
        </g>

        {/* WORM GEAR */}
        <g transform="translate(600,10)">
          <text x={80} y={10} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Worm & Wheel</text>
          {/* Worm (cylindrical with helix) */}
          <rect x={30} y={50} width={30} height={85} rx={8} fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
          {[55,65,75,85,95,105,115,125].map((y,i)=>(
            <line key={i} x1={30} y1={y} x2={60} y2={y+8} stroke="#7a2e00" strokeWidth="1.5" opacity="0.6"/>
          ))}
          <text x={45} y={148} textAnchor="middle" fontSize="8" fill="#7a2e00">Worm</text>
          {/* Wheel (partial arc) */}
          <path d="M 65 90 A 55 55 0 0 1 155 90" fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
          <path d="M 65 90 L 65 110 A 55 55 0 0 0 155 110 L 155 90 Z" fill="#c45a10" stroke="#7a2e00" strokeWidth="1.5" opacity="0.6"/>
          {Array.from({length:8},(_,i)=>{
            const a = -10+i*14;
            const rad = a*Math.PI/180;
            const r=55;
            return <line key={i}
              x1={110+r*Math.cos(Math.PI+rad)} y1={90+r*Math.sin(Math.PI+rad)}
              x2={110+(r+10)*Math.cos(Math.PI+rad)} y2={90+(r+10)*Math.sin(Math.PI+rad)}
              stroke="#7a2e00" strokeWidth="2" opacity="0.7"/>
          })}
          <text x={110} y={148} textAnchor="middle" fontSize="8" fill="#7a2e00">Wheel</text>
          <text x={80} y={175} textAnchor="middle" fontSize="9" fill="#a04010">Perpendicular shafts</text>
          <text x={80} y={187} textAnchor="middle" fontSize="9" fill="#a04010">Very high ratio (5:1–100:1)</text>
          <text x={80} y={199} textAnchor="middle" fontSize="9" fill="#a04010">Self-locking possible</text>
          <text x={80} y={211} textAnchor="middle" fontSize="9" fill="#a04010">Low efficiency (~50–90%)</text>
        </g>

        {/* RACK & PINION */}
        <g transform="translate(770,20)">
          <text x={60} y={10} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Rack & Pinion</text>
          {/* Pinion */}
          <Gear cx={60} cy={75} r={32} teeth={10} fill="#d4874a" stroke="#7a2e00" strokeWidth={1.8}/>
          {/* Rack */}
          <rect x={10} y={112} width={100} height={18} rx={2} fill="#c45a10" stroke="#7a2e00" strokeWidth="2"/>
          {[14,24,34,44,54,64,74,84,94].map((x,i)=>(
            <rect key={i} x={x} y={106} width={8} height={8} rx={1} fill="#7a2e00" opacity="0.75"/>
          ))}
          <text x={60} y={155} textAnchor="middle" fontSize="9" fill="#a04010">Rotation → linear</text>
          <text x={60} y={167} textAnchor="middle" fontSize="9" fill="#a04010">Steering, CNC, lifts</text>
          <text x={60} y={179} textAnchor="middle" fontSize="9" fill="#a04010">Rack = infinite radius</text>
        </g>

        {/* Comparison row */}
        <rect x={10} y={240} width={880} height={62} rx={4} fill="#fdf0e4" stroke="#e8b07a"/>
        <text x={30} y={257} fontSize="9" fontWeight="700" fill="#7a2e00">Efficiency comparison:</text>
        {[
          {x:160, lbl:'Spur', eff:'98–99%', use:'Clocks, gearboxes'},
          {x:320, lbl:'Helical', eff:'96–98%', use:'Cars, turbines'},
          {x:490, lbl:'Bevel', eff:'95–98%', use:'Differentials, drills'},
          {x:660, lbl:'Worm', eff:'50–90%', use:'Lifts, conveyors'},
          {x:820, lbl:'Rack/Pinion', eff:'~95%', use:'Steering, CNC'},
        ].map((b,i)=>(
          <g key={i}>
            <text x={b.x} y={257} textAnchor="middle" fontSize="9" fontWeight="700" fill="#a04010">{b.lbl}</text>
            <text x={b.x} y={270} textAnchor="middle" fontSize="8.5" fill="#7a4a28">η: {b.eff}</text>
            <text x={b.x} y={283} textAnchor="middle" fontSize="8.5" fill="#7a4a28">{b.use}</text>
            {i<4 && <line x1={b.x+80} y1={244} x2={b.x+80} y2={298} stroke="#e8c89a" strokeWidth="1"/>}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Gear type determines shaft orientation, efficiency, noise level, and whether the drive can be self-locking</div>
    </div>
  );
}

// ── Diagram 3: Simple vs Compound Gear Train ─────────────────────────
function DiagramSimpleCompound() {
  const W = 900, H = 340;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Simple Gear Train vs Compound Gear Train — Ratio Comparison</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arrowSC" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* ── SIMPLE GEAR TRAIN (top half) ── */}
        <text x={220} y={18} textAnchor="middle" fontSize="12" fontWeight="700" fill="#7a2e00">Simple Gear Train</text>
        <text x={220} y={30} textAnchor="middle" fontSize="9" fill="#a04010">One gear per shaft — idler gears change direction only, not ratio</text>

        {/* 4 gears in a simple train */}
        {[
          {cx:55,  r:38, t:12, lbl:'A', N:'N=12'},
          {cx:142, r:28, t:9,  lbl:'B', N:'N=9 (idler)'},
          {cx:216, r:28, t:9,  lbl:'C', N:'N=9 (idler)'},
          {cx:302, r:52, t:16, lbl:'D', N:'N=16'},
        ].map((g,i)=>(
          <g key={i}>
            <Gear cx={g.cx} cy={100} r={g.r} teeth={g.t}
              fill={i===0?'#c45a10':i===3?'#c45a10':'#e8b07a'}
              stroke="#7a2e00" strokeWidth={2} label={g.lbl} labelColor="#fdf4ec"/>
            <text x={g.cx} y={160} textAnchor="middle" fontSize="8" fill="#7a4a28">{g.N}</text>
          </g>
        ))}

        {/* Simple train VR annotation */}
        <rect x={370} y={65} width={220} height={70} rx={5} fill="#fdf0e4" stroke="#c45a10"/>
        <text x={480} y={82} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Velocity Ratio</text>
        <text x={480} y={97} textAnchor="middle" fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">i = N_last / N_first</text>
        <text x={480} y={112} textAnchor="middle" fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">  = 16 / 12 = 1.33</text>
        <text x={480} y={126} textAnchor="middle" fontSize="8" fill="#7a4a28">Idlers cancel out in ratio</text>

        {/* ── COMPOUND GEAR TRAIN (bottom half) ── */}
        <line x1={20} y1={185} x2={880} y2={185} stroke="#e8c89a" strokeDasharray="5,4"/>
        <text x={220} y={200} textAnchor="middle" fontSize="12" fontWeight="700" fill="#7a2e00">Compound Gear Train</text>
        <text x={220} y={212} textAnchor="middle" fontSize="9" fill="#a04010">Two gears on intermediate shafts — ratios multiply</text>

        {/* Shaft 1: input gear A (small) */}
        <Gear cx={55} cy={280} r={28} teeth={9} fill="#c45a10" stroke="#7a2e00" strokeWidth={2} label="A"/>
        <text x={55} y={323} textAnchor="middle" fontSize="8" fill="#7a4a28">N_A=12</text>

        {/* Shaft 2: B (large, meshes with A) + C (small, drives D) — on same shaft */}
        <Gear cx={155} cy={280} r={50} teeth={16} fill="#d4874a" stroke="#7a2e00" strokeWidth={2} label="B"/>
        <Gear cx={155} cy={280} r={22} teeth={7} fill="#c45a10" stroke="#7a2e00" strokeWidth={1.8} label="C" labelColor="#fdf4ec"/>
        {/* shaft indicator */}
        <line x1={155} y1={228} x2={155} y2={253} stroke="#7a2e00" strokeWidth="5"/>
        <text x={155} y={323} textAnchor="middle" fontSize="8" fill="#7a4a28">N_B=48, N_C=12</text>
        <rect x={120} y={315} width={70} height={12} rx={2} fill="#fdf0e4" stroke="#e8b07a"/>
        <text x={155} y={324} textAnchor="middle" fontSize="7" fill="#7a2e00">same shaft</text>

        {/* Shaft 3: D (large output) */}
        <Gear cx={268} cy={280} r={50} teeth={16} fill="#d4874a" stroke="#7a2e00" strokeWidth={2} label="D"/>
        <text x={268} y={338} textAnchor="middle" fontSize="8" fill="#7a4a28">N_D=48</text>

        {/* Compound train VR annotation */}
        <rect x={370} y={215} width={260} height={100} rx={5} fill="#fdf0e4" stroke="#c45a10"/>
        <text x={500} y={232} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Compound Velocity Ratio</text>
        <text x={500} y={248} textAnchor="middle" fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">i = (N_B/N_A) × (N_D/N_C)</text>
        <text x={500} y={263} textAnchor="middle" fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">  = (48/12) × (48/12)</text>
        <text x={500} y={278} textAnchor="middle" fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">  = 4 × 4 = 16 : 1</text>
        <text x={500} y={293} textAnchor="middle" fontSize="8" fill="#7a4a28">Each stage multiplies — huge ratios in small space</text>
        <text x={500} y={306} textAnchor="middle" fontSize="8" fill="#7a4a28">Same ratio needs N=192 single-stage gear!</text>

        {/* Key advantage callout */}
        <rect x={650} y={215} width={240} height={100} rx={5} fill="#fdf4ec" stroke="#e8b07a"/>
        <text x={770} y={232} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Why Compound?</text>
        <text x={770} y={248} textAnchor="middle" fontSize="8.5" fill="#a04010">► Large ratios from small gears</text>
        <text x={770} y={262} textAnchor="middle" fontSize="8.5" fill="#a04010">► Compact — less space needed</text>
        <text x={770} y={276} textAnchor="middle" fontSize="8.5" fill="#a04010">► Multiple output speeds</text>
        <text x={770} y={290} textAnchor="middle" fontSize="8.5" fill="#a04010">► Better efficiency per stage</text>
        <text x={770} y={304} textAnchor="middle" fontSize="8.5" fill="#a04010">► Used in all automotive gearboxes</text>
      </svg>
      <div style={S.dCaption}>Compound gear trains are the basis of all multi-speed gearboxes — idlers in a simple train only reverse direction, they do not change the overall ratio</div>
    </div>
  );
}

// ── Diagram 4: Epicyclic (Planetary) Gear Train ──────────────────────
function DiagramPlanetary() {
  const W = 900, H = 370;
  const cx = 230, cy = 180;
  const r_sun = 42, r_planet = 38, r_ring = r_sun + 2*r_planet;

  // Planet centres (3 planets equally spaced)
  const planets = [0, 120, 240].map(deg => {
    const rad = deg * Math.PI / 180;
    return {
      x: cx + (r_sun + r_planet) * Math.cos(rad),
      y: cy + (r_sun + r_planet) * Math.sin(rad),
    };
  });

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Epicyclic (Planetary) Gear Train — Sun, Planet & Ring Arrangement</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arrowPL" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* Ring gear (annulus) — drawn as thick ring */}
        <circle cx={cx} cy={cy} r={r_ring+14} fill="#fdf4ec" stroke="#7a2e00" strokeWidth="3"/>
        <circle cx={cx} cy={cy} r={r_ring} fill="none" stroke="#c45a10" strokeWidth="22" opacity="0.35"/>
        {/* Ring teeth on inner face */}
        {Array.from({length:36},(_,i)=>{
          const a = i * 10 * Math.PI/180;
          return <line key={i}
            x1={cx+(r_ring-6)*Math.cos(a)} y1={cy+(r_ring-6)*Math.sin(a)}
            x2={cx+(r_ring+6)*Math.cos(a)} y2={cy+(r_ring+6)*Math.sin(a)}
            stroke="#7a2e00" strokeWidth="2.5" opacity="0.6"/>
        })}
        <text x={cx} y={cy-r_ring-20} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Ring Gear (Annulus) R</text>

        {/* Planet carrier arms */}
        {planets.map((p, i) => (
          <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y}
            stroke="#a04010" strokeWidth="5" strokeLinecap="round" opacity="0.4"/>
        ))}

        {/* Planet gears */}
        {planets.map((p, i) => (
          <Gear key={i} cx={p.x} cy={p.y} r={r_planet} teeth={12}
            fill="#d4874a" stroke="#7a2e00" strokeWidth={2}
            label={`P${i+1}`} labelColor="#fdf4ec"/>
        ))}

        {/* Sun gear */}
        <Gear cx={cx} cy={cy} r={r_sun} teeth={14}
          fill="#c45a10" stroke="#7a2e00" strokeWidth={2.5}
          label="S" labelColor="#fdf4ec"/>

        {/* Carrier hub */}
        <circle cx={cx} cy={cy} r={10} fill="#fdf0e4" stroke="#7a2e00" strokeWidth="2"/>
        <text x={cx} y={cy+r_ring+28} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Planet Carrier (C)</text>

        {/* Rotation arrow on sun */}
        <path d={`M ${cx+r_sun+10} ${cy} A ${r_sun+10} ${r_sun+10} 0 0 1 ${cx} ${cy-r_sun-10}`}
          fill="none" stroke="#c45a10" strokeWidth="2" markerEnd="url(#arrowPL)"/>

        {/* Labels panel */}
        <rect x={490} y={20} width={395} height={330} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={687} y={40} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Planetary Gear — Operating Modes</text>
        <line x1={500} y1={46} x2={876} y2={46} stroke="#e8b07a"/>

        {/* Mode table */}
        {[
          ['Input', 'Fixed', 'Output', 'Ratio Formula'],
          ['Sun', 'Ring', 'Carrier', 'i = 1 + R/S'],
          ['Sun', 'Carrier', 'Ring', 'i = −R/S'],
          ['Ring', 'Sun', 'Carrier', 'i = 1 + S/R'],
          ['Ring', 'Carrier', 'Sun', 'i = −S/R'],
          ['Carrier', 'Sun', 'Ring', 'i = 1/(1+S/R)'],
          ['Carrier', 'Ring', 'Sun', 'i = 1/(1+R/S)'],
        ].map((row, ri) => (
          <g key={ri}>
            {row.map((cell, ci) => (
              <text key={ci}
                x={505 + ci*96} y={63 + ri*20}
                fontSize={ri===0?'8':'8.5'}
                fontWeight={ri===0?'700':'400'}
                fill={ri===0?'#7a2e00':'#a04010'}
                fontFamily={ri>0?"'Courier New',monospace":"inherit"}>
                {cell}
              </text>
            ))}
            {ri > 0 && ri < 6 && (
              <line x1={500} y1={67+ri*20} x2={878} y2={67+ri*20} stroke="#e8c89a" strokeWidth="0.7"/>
            )}
          </g>
        ))}

        <line x1={500} y1={200} x2={878} y2={200} stroke="#e8b07a" strokeWidth="1.5"/>
        <text x={687} y={216} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Key Properties</text>
        {[
          'S + 2P = R  (tooth count constraint)',
          'Coaxial input & output (same axis)',
          'High power density — multiple load paths',
          'Automatic gearboxes use multiple stages',
          'Two inputs → differential action',
          'Very high ratios possible (100:1+)',
          'Used in: automatic gearboxes, hub gears,',
          '  wind turbines, robot joints',
        ].map((line,i)=>(
          <text key={i} x={505} y={232+i*14} fontSize="8.5" fill={i===0?'#7a2e00':'#a04010'}
            fontWeight={i===0?'700':'400'}>{line}</text>
        ))}
      </svg>
      <div style={S.dCaption}>Planetary gears achieve different ratios by fixing different members · all three members (sun, ring, carrier) can serve as input, output, or fixed element</div>
    </div>
  );
}

// ── Diagram 5: Gear Ratio & Speed/Torque Chart ───────────────────────
function DiagramRatioChart() {
  const W = 900, H = 280;
  const barBase = 210, barMaxH = 150, maxVal = 8;

  const data = [
    { label:'Stage 1\nN₁=12→N₂=48', ratio:4.0, speed:250, torque:4.0 },
    { label:'Stage 2\nN₃=12→N₄=36', ratio:3.0, speed:83,  torque:12  },
    { label:'Stage 3\nN₅=10→N₆=40', ratio:4.0, speed:21,  torque:48  },
    { label:'Overall\n12→48→36→40', ratio:48,  speed:21,  torque:48  },
  ];

  const barW = 80, gap = 130;
  const startX = 80;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Three-Stage Compound Gear Train — Cascading Speed & Torque</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arrowRC" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* Y axis */}
        <line x1={55} y1={55} x2={55} y2={barBase} stroke="#e8b07a" strokeWidth="1.5"/>
        <line x1={55} y1={barBase} x2={700} y2={barBase} stroke="#e8b07a" strokeWidth="1.5"/>
        {[0,2,4,6,8].map((v,i)=>{
          const y = barBase - (v/maxVal)*barMaxH;
          return (
            <g key={i}>
              <line x1={50} y1={y} x2={700} y2={y} stroke="#e8c89a" strokeWidth="0.7" strokeDasharray="3,3"/>
              <text x={48} y={y+4} textAnchor="end" fontSize="8" fill="#7a4a28">{v}</text>
            </g>
          );
        })}
        <text x={22} y={140} textAnchor="middle" fontSize="9" fill="#7a2e00" transform="rotate(-90,22,140)">Stage Ratio</text>

        {/* Bars per stage */}
        {data.slice(0,3).map((d,i)=>{
          const x = startX + i*gap;
          const h = Math.min((d.ratio/maxVal)*barMaxH, barMaxH);
          const lines = d.label.split('\n');
          return (
            <g key={i}>
              <rect x={x} y={barBase-h} width={barW} height={h}
                fill="#c45a10" opacity={0.65+i*0.1} rx={3}/>
              <text x={x+barW/2} y={barBase-h-6} textAnchor="middle"
                fontSize="10" fontWeight="700" fill="#7a2e00">{d.ratio}×</text>
              {lines.map((l,li)=>(
                <text key={li} x={x+barW/2} y={barBase+14+li*12}
                  textAnchor="middle" fontSize="8" fill="#7a4a28">{l}</text>
              ))}
            </g>
          );
        })}

        {/* Flow arrows between stages */}
        {[0,1].map(i=>{
          const x1 = startX + i*gap + barW + 4;
          const x2 = startX + (i+1)*gap - 4;
          return (
            <line key={i} x1={x1} y1={barBase-30} x2={x2} y2={barBase-30}
              stroke="#c45a10" strokeWidth="1.5" markerEnd="url(#arrowRC)"/>
          );
        })}

        {/* Overall result box */}
        <rect x={460} y={55} width={170} height={155} rx={5} fill="#fdf0e4" stroke="#c45a10" strokeWidth="2"/>
        <text x={545} y={74} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Overall Result</text>
        <line x1={468} y1={80} x2={622} y2={80} stroke="#e8b07a"/>
        <text x={545} y={98} textAnchor="middle" fontSize="9" fill="#a04010">i_total = 4×3×4 = 48</text>
        <text x={545} y={115} textAnchor="middle" fontSize="9" fill="#a04010">Input:  1000 rpm</text>
        <text x={545} y={130} textAnchor="middle" fontSize="9" fill="#a04010">Output:  20.8 rpm</text>
        <text x={545} y={147} textAnchor="middle" fontSize="9" fill="#a04010">Torque ×48 (less η)</text>
        <text x={545} y={165} textAnchor="middle" fontSize="8" fill="#7a4a28">η = η₁×η₂×η₃</text>
        <text x={545} y={178} textAnchor="middle" fontSize="8" fill="#7a4a28">≈ 0.98³ ≈ 0.94</text>
        <text x={545} y={198} textAnchor="middle" fontSize="8" fontStyle="italic" fill="#7a4a28">Formula: i_total = ∏(N_driven/N_driver)</text>

        {/* Power conservation note */}
        <rect x={645} y={55} width={245} height={155} rx={5} fill="#fdf4ec" stroke="#e8b07a"/>
        <text x={767} y={74} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Power Conservation</text>
        <line x1={653} y1={80} x2={882} y2={80} stroke="#e8b07a"/>
        {[
          'P_in ≈ P_out  (ideal)',
          'τ_out = τ_in × i × η',
          'ω_out = ω_in / i',
          '',
          'Speed ↓ → Torque ↑',
          'Speed ↑ → Torque ↓',
          '',
          'P = τ × ω = constant',
          '(excluding losses)',
        ].map((l,i)=>(
          <text key={i} x={653} y={96+i*14} fontSize="8.5"
            fontFamily="'Courier New',monospace"
            fill={l.includes('↓')||l.includes('↑')?'#7a2e00':'#a04010'}
            fontWeight={l.includes('↓')||l.includes('↑')?'700':'400'}>{l}</text>
        ))}
      </svg>
      <div style={S.dCaption}>Each stage multiplies both the gear ratio and torque — overall ratio = product of all stage ratios · efficiency degrades with each additional mesh</div>
    </div>
  );
}

// ── Diagram 6: Gear Geometry — tooth profile & terminology ───────────
function DiagramToothGeometry() {
  const W = 900, H = 320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Involute Tooth Profile — Geometry & Key Dimensions</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Draw a single tooth cross-section — detailed */}
        {/* Base circles and pitch circle */}
        <circle cx={200} cy={220} r={140} fill="none" stroke="#e8c89a" strokeWidth="1" strokeDasharray="4,3"/>
        <circle cx={200} cy={220} r={120} fill="none" stroke="#e8b07a" strokeWidth="1.5" strokeDasharray="5,3"/>
        <circle cx={200} cy={220} r={100} fill="none" stroke="#d4874a" strokeWidth="1" strokeDasharray="3,3"/>
        <circle cx={200} cy={220} r={85} fill="none" stroke="#c45a10" strokeWidth="1" strokeDasharray="3,3"/>

        {/* Tooth profile (one tooth prominently shown) */}
        <path d="M 188,80 Q 172,82 170,100 L 168,118 Q 165,128 162,140
                 L 160,160 Q 160,176 162,180 L 170,186 Q 180,190 200,190
                 Q 220,190 230,186 L 238,180 Q 240,176 240,160
                 L 238,140 Q 235,128 232,118 L 230,100 Q 228,82 212,80 Z"
          fill="#c45a10" stroke="#7a2e00" strokeWidth="2.5" opacity="0.9"/>

        {/* Partial gear rim to give context */}
        <path d="M 100,190 Q 80,210 82,240 Q 84,260 100,270 L 320,270 Q 336,260 318,240 Q 316,210 300,190"
          fill="#d4874a" stroke="#7a2e00" strokeWidth="2" opacity="0.4"/>

        {/* Dimension lines */}
        {/* Addendum */}
        <line x1={260} y1={80} x2={260} y2={120} stroke="#7a2e00" strokeWidth="1.5"/>
        <line x1={256} y1={80} x2={264} y2={80} stroke="#7a2e00" strokeWidth="1.5"/>
        <line x1={256} y1={120} x2={264} y2={120} stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={268} y={92} fontSize="9" fontWeight="700" fill="#7a2e00">Addendum</text>
        <text x={268} y={104} fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">a = m</text>

        {/* Dedendum */}
        <line x1={260} y1={120} x2={260} y2={175} stroke="#a04010" strokeWidth="1.5"/>
        <line x1={256} y1={175} x2={264} y2={175} stroke="#a04010" strokeWidth="1.5"/>
        <text x={268} y={142} fontSize="9" fontWeight="700" fill="#a04010">Dedendum</text>
        <text x={268} y={154} fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">b = 1.25m</text>

        {/* Whole depth */}
        <line x1={340} y1={80} x2={340} y2={175} stroke="#c45a10" strokeWidth="1.5"/>
        <line x1={336} y1={80} x2={344} y2={80} stroke="#c45a10" strokeWidth="1.5"/>
        <line x1={336} y1={175} x2={344} y2={175} stroke="#c45a10" strokeWidth="1.5"/>
        <text x={348} y={122} fontSize="9" fontWeight="700" fill="#c45a10">Whole Depth</text>
        <text x={348} y={134} fontSize="9" fontFamily="'Courier New',monospace" fill="#c45a10">h = 2.25m</text>

        {/* Pitch circle annotation */}
        <line x1={200} y1={120} x2={140} y2={70} stroke="#e8b07a" strokeWidth="1" strokeDasharray="3,2"/>
        <text x={90} y={65} fontSize="8.5" fill="#a04010" fontStyle="italic">Pitch circle</text>
        <text x={90} y={77} fontSize="8.5" fontFamily="'Courier New',monospace" fill="#a04010">d = m × N</text>

        {/* Addendum circle */}
        <line x1={200} y1={80} x2={130} y2={35} stroke="#e8c89a" strokeWidth="1" strokeDasharray="3,2"/>
        <text x={70} y={30} fontSize="8.5" fill="#7a4a28" fontStyle="italic">Addendum circle</text>
        <text x={70} y={42} fontSize="8.5" fontFamily="'Courier New',monospace" fill="#7a4a28">d_a = m(N+2)</text>

        {/* Dedendum circle */}
        <line x1={182} y1={175} x2={90} y2={195} stroke="#e8b07a" strokeWidth="1" strokeDasharray="3,2"/>
        <text x={8} y={196} fontSize="8.5" fill="#7a4a28" fontStyle="italic">Dedendum circle</text>
        <text x={8} y={208} fontSize="8.5" fontFamily="'Courier New',monospace" fill="#7a4a28">d_f = m(N-2.5)</text>

        {/* Circular pitch */}
        <path d="M 162,175 A 25 25 0 0 1 238,175" fill="none" stroke="#7a2e00" strokeWidth="2" strokeDasharray="4,2"/>
        <text x={200} y={235} textAnchor="middle" fontSize="8.5" fill="#7a2e00">← Circular pitch p = πm →</text>

        {/* Pressure angle diagram on right side */}
        <rect x={480} y={20} width={400} height={290} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={680} y={40} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Pressure Angle & Involute Profile</text>
        <line x1={490} y1={46} x2={872} y2={46} stroke="#e8b07a"/>

        {/* Involute force diagram */}
        <circle cx={620} cy={165} r={70} fill="none" stroke="#e8b07a" strokeWidth="1.5" strokeDasharray="4,3"/>
        <circle cx={620} cy={165} r={55} fill="none" stroke="#e8b07a" strokeWidth="1" strokeDasharray="3,3"/>

        {/* Gear bodies */}
        <circle cx={555} cy={165} r={22} fill="#c45a10" opacity="0.7" stroke="#7a2e00" strokeWidth="2"/>
        <circle cx={685} cy={165} r={22} fill="#d4874a" opacity="0.7" stroke="#7a2e00" strokeWidth="2"/>

        {/* Pressure line (line of action) */}
        <line x1={530} y1={140} x2={710} y2={190} stroke="#7a2e00" strokeWidth="2"/>
        <text x={590} y={130} fontSize="8.5" fontWeight="700" fill="#7a2e00">Line of Action</text>

        {/* Common tangent (perpendicular to line of action) */}
        <line x1={605} y1={200} x2={635} y2={130} stroke="#c45a10" strokeWidth="1.5" strokeDasharray="4,2"/>
        <text x={640} y={128} fontSize="8" fill="#c45a10">Common</text>
        <text x={640} y={140} fontSize="8" fill="#c45a10">tangent</text>

        {/* Pressure angle arc */}
        <path d="M 620,145 A 20 20 0 0 1 638,155" fill="none" stroke="#a04010" strokeWidth="1.5"/>
        <text x={645} y={150} fontSize="9" fontWeight="700" fill="#a04010">φ=20°</text>

        {/* Annotations */}
        {[
          [490, 225, 'φ = 20° standard pressure angle (also 14.5° older systems)'],
          [490, 241, 'Involute profile ensures constant velocity ratio during mesh'],
          [490, 257, 'Contact along line of action — pure rolling at pitch point'],
          [490, 273, 'Normal force acts along line of action at angle φ to tangent'],
          [490, 289, 'Higher φ → stronger teeth but more bearing load'],
        ].map(([x,y,txt],i)=>(
          <text key={i} x={x} y={y} fontSize="8.5" fill="#a04010">{txt}</text>
        ))}
      </svg>
      <div style={S.dCaption}>Involute tooth profile guarantees constant velocity ratio regardless of centre distance variation · module m is the fundamental sizing parameter</div>
    </div>
  );
}

// ── Diagram 7: Reverted & Gear Train Applications ────────────────────
function DiagramRevertedAndApps() {
  const W = 900, H = 300;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Reverted Gear Train — Input & Output on Same Axis</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arrowRV" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* Shaft 1 (input) — left, centre y=150 */}
        {/* Shaft 2 — intermediate */}
        {/* Shaft 3 = Shaft 1 axis (output, concentric) */}

        {/* Gear A on shaft 1 (small) */}
        <Gear cx={130} cy={150} r={30} teeth={10} fill="#c45a10" stroke="#7a2e00" strokeWidth={2} label="A"/>
        <text x={130} y={200} textAnchor="middle" fontSize="8" fill="#7a4a28">N_A = 20</text>

        {/* Gear B on shaft 2 (large, meshes with A) */}
        <Gear cx={240} cy={150} r={60} teeth={20} fill="#d4874a" stroke="#7a2e00" strokeWidth={2} label="B"/>
        <text x={240} y={225} textAnchor="middle" fontSize="8" fill="#7a4a28">N_B = 40</text>

        {/* Gear C on shaft 2 (small, same shaft as B) */}
        <Gear cx={240} cy={150} r={25} teeth={8} fill="#c45a10" stroke="#7a2e00" strokeWidth={2} label="C"/>
        {/* Shaft 2 physical rod */}
        <line x1={240} y1={88} x2={240} y2={75} stroke="#7a2e00" strokeWidth="5"/>
        <text x={240} y={235} textAnchor="middle" fontSize="7" fill="#7a2e00">B & C: same shaft</text>

        {/* Gear D on shaft 3 (output, same axis as shaft 1) */}
        <Gear cx={130} cy={150} r={55} teeth={18} fill="#d4874a" stroke="#7a2e00" strokeWidth={2} label="D"/>
        <text x={130} y={215} textAnchor="middle" fontSize="8" fill="#7a4a28">N_D = 36 (output)</text>

        {/* Shaft lines */}
        <line x1={130} y1={150} x2={60} y2={150} stroke="#7a2e00" strokeWidth="5"/>
        <text x={42} y={145} textAnchor="end" fontSize="9" fontWeight="700" fill="#c45a10">IN →</text>

        <line x1={130} y1={150} x2={20} y2={150} stroke="#7a2e00" strokeWidth="3" strokeDasharray="5,3"/>
        <text x={12} y={165} textAnchor="middle" fontSize="8" fill="#a04010">← OUT</text>

        {/* Constraint annotation */}
        <rect x={10} y={250} width={340} height={40} rx={4} fill="#fdf0e4" stroke="#e8b07a"/>
        <text x={180} y={266} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">Constraint: r_A + r_B = r_C + r_D</text>
        <text x={180} y={280} textAnchor="middle" fontSize="8.5" fontFamily="'Courier New',monospace" fill="#a04010">30+60 = 25+55 = 90 ✓  Centre distance must be equal</text>

        {/* VR calculation */}
        <rect x={370} y={30} width={250} height={130} rx={5} fill="#fdf0e4" stroke="#c45a10"/>
        <text x={495} y={48} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Reverted Gear Train VR</text>
        <line x1={378} y1={54} x2={612} y2={54} stroke="#e8b07a"/>
        {[
          'i = (N_B × N_D) / (N_A × N_C)',
          '  = (40 × 36) / (20 × 8)',
          '  = 1440 / 160',
          '  = 9 : 1 reduction',
          '',
          'Input & output COAXIAL',
          '→ same axis, compact design',
        ].map((l,i)=>(
          <text key={i} x={380} y={68+i*14} fontSize="9"
            fontFamily="'Courier New',monospace"
            fill={i===3?'#7a2e00':i===5||i===6?'#a04010':'#a04010'}
            fontWeight={i===3||i===5?'700':'400'}>{l}</text>
        ))}

        {/* Applications panel */}
        <rect x={640} y={30} width={250} height={260} rx={5} fill="#fdf4ec" stroke="#e8b07a"/>
        <text x={765} y={48} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Gear Train Applications</text>
        <line x1={648} y1={54} x2={882} y2={54} stroke="#e8b07a"/>
        {[
          ['Clock / Watch', 'Simple: precise ratios,\nlow torque, high VR'],
          ['Automotive gearbox', 'Compound + synchromesh:\nmultiple speed stages'],
          ['Differential', 'Planetary + bevel:\nsplit torque to wheels'],
          ['Lathe headstock', 'Compound: wide speed\nrange, heavy cuts'],
          ['Winch / hoist', 'Compound/worm:\nhigh torque, self-lock'],
          ['Robot joint', 'Planetary: high ratio,\ncompact, coaxial'],
        ].map(([title, desc], i) => {
          const lines = desc.split('\n');
          return (
            <g key={i}>
              <text x={650} y={72+i*33} fontSize="9" fontWeight="700" fill="#7a2e00">► {title}</text>
              {lines.map((l,li)=>(
                <text key={li} x={660} y={84+i*33+li*12} fontSize="8" fill="#a04010">{l}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>Reverted gear trains are used wherever a compact coaxial arrangement is needed — the constraint that centre distances must be equal tightly couples gear sizing</div>
    </div>
  );
}

// ── Diagram 8: Gear Train Efficiency & Losses ────────────────────────
function DiagramEfficiency() {
  const W = 900, H = 260;

  const losses = [
    { label:'Sliding\nFriction', pct:1.5, desc:'Tooth flanks slide during approach/recess; dominant loss source', x:65 },
    { label:'Rolling\nFriction', pct:0.4, desc:'Pure rolling at pitch point — low loss', x:210 },
    { label:'Churning\n(Oil)', pct:0.8, desc:'Teeth churning through oil bath (high-speed)', x:355 },
    { label:'Bearing\nFriction', pct:0.6, desc:'Shaft support bearing losses', x:500 },
    { label:'Windage', pct:0.3, desc:'Air resistance on fast rotating gears', x:645 },
    { label:'Seal\nFriction', pct:0.2, desc:'Lip seals on shafts and housings', x:780 },
  ];

  const maxPct = 3, barH = 100, barW = 70, barBase = 170;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Power Loss Sources in Gear Trains — Per-Mesh Typical Percentages</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Efficiency banner */}
        <rect x={20} y={8} width={860} height={28} rx={4} fill="#fdf0e4" stroke="#c45a10"/>
        <text x={450} y={21} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">
          Spur/helical mesh efficiency ≈ 98–99% per stage · Worm gear 50–90% · Overall: η_total = η₁ × η₂ × … × ηₙ
        </text>
        <text x={450} y={32} textAnchor="middle" fontSize="9" fill="#a04010">
          η = 1 − Σ(losses)  ·  P_loss = P_in × (1 − η)  ·  manifests as heat in housing
        </text>

        {/* Axis */}
        <line x1={42} y1={42} x2={42} y2={barBase} stroke="#e8b07a" strokeWidth="1.5"/>
        <line x1={42} y1={barBase} x2={875} y2={barBase} stroke="#e8b07a" strokeWidth="1.5"/>
        {[0,1,2,3].map((v,i)=>{
          const y = barBase - (v/maxPct)*barH;
          return (
            <g key={i}>
              <line x1={38} y1={y} x2={875} y2={y} stroke="#e8c89a" strokeWidth="0.7" strokeDasharray="3,3"/>
              <text x={36} y={y+4} textAnchor="end" fontSize="8" fill="#7a4a28">{v}%</text>
            </g>
          );
        })}

        {/* Bars */}
        {losses.map((l,i)=>{
          const h = (l.pct/maxPct)*barH;
          const lines = l.label.split('\n');
          return (
            <g key={i}>
              <rect x={l.x} y={barBase-h} width={barW} height={h}
                fill="#c45a10" opacity={0.55+i*0.07} rx={3}/>
              <text x={l.x+barW/2} y={barBase-h-6} textAnchor="middle"
                fontSize="10" fontWeight="700" fill="#7a2e00">{l.pct}%</text>
              {lines.map((line,li)=>(
                <text key={li} x={l.x+barW/2} y={barBase+14+li*12}
                  textAnchor="middle" fontSize="8" fill="#7a4a28">{line}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>Sliding friction on tooth flanks dominates losses — reduced by gear oil, surface finish, and operating near pitch point where sliding velocity is lowest</div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────
export default function GearTrains() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Gear Trains in Mechanical Engineering</h1>
        
      <p style={S.p}>
        
        A <strong>gear train</strong> is an assembly of meshing gears arranged to transmit motion and power between shafts,
        providing a change in speed, torque, or direction of rotation. Unlike belt drives, gear trains transmit force through
        direct tooth contact — eliminating slip entirely and achieving precise velocity ratios. They are fundamental to
        clocks, automotive transmissions, industrial machinery, robotics, and aerospace systems.
      </p>
 <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                    <img src={GearTrainsImg} alt="gear train" width="55%"/>
                                </div>  
      <InfoBox title="Quick Reference — Core Formulae" items={[
        "Gear ratio (velocity ratio):  i = N₂/N₁ = ω₁/ω₂ = r₂/r₁  (N = teeth, ω = angular velocity, r = pitch radius)",
        "Module:  m = d/N = p/π  (d = pitch diameter mm, p = circular pitch mm) — must match for meshing gears",
        "Compound train:  i_total = (N₂/N₁) × (N₄/N₃) × … = product of all stage ratios",
        "Torque:  τ_out = τ_in × i × η  where η = overall efficiency",
        "Power:  P = τ × ω  (constant along train, minus losses at each mesh)",
        "Centre distance:  C = m(N₁+N₂)/2  (external pair)  ·  C = m(N₂−N₁)/2  (internal pair)",
        "Planetary (sun fixed):  i = 1 + N_R/N_S  where R = ring teeth, S = sun teeth",
        "Efficiency per spur/helical stage ≈ 98–99%  ·  Worm gear ≈ 50–90%",
      ]}/>

      <h2 style={S.h2}>Simple Gear Pair — Anatomy & Fundamentals</h2>
      <p style={S.p}>
        Two meshing gears form the fundamental unit of any gear train. The <em>driver</em> gear receives input torque;
        the <em>driven</em> gear delivers output torque. Their pitch circles roll without slipping at the pitch point,
        and the involute tooth profile guarantees a constant velocity ratio throughout contact.
      </p>
      <DiagramSimplePair />

      <h2 style={S.h2}>Gear Types</h2>
      <p style={S.p}>
        Different gear geometries suit different shaft arrangements, power levels, and operating conditions.
        Spur and helical gears dominate parallel-shaft applications; bevel gears handle intersecting shafts;
        worm gears achieve very high ratios in a compact cross-shaft package.
      </p>
      <DiagramGearTypes />
      <Table2 rows={[
        ["Spur gear","Straight teeth parallel to shaft axis · simplest and cheapest to manufacture\nHigh efficiency (98–99%) · noisy at high speeds due to sudden tooth engagement\nUsed in: clocks, gearboxes, conveyors, pumps"],
        ["Helical gear","Teeth cut at helix angle (15–30°) · multiple teeth in contact at once\nSmoother, quieter operation · generates axial thrust requiring thrust bearings\nDouble-helical (herringbone) cancels axial thrust · used in turbines, vehicles"],
        ["Bevel gear","Conical gear for intersecting shafts · typically 90° shaft angle\nStraight, spiral, or Zerol bevel varieties · spiral bevel quieter and stronger\nUsed in: automotive differentials, hand drills, marine drives"],
        ["Worm & worm wheel","Worm is a screw thread meshing with worm wheel · perpendicular non-intersecting shafts\nVery high ratios (5:1–100:1) in one stage · can be self-locking (prevents back-driving)\nLow efficiency (50–90%) due to sliding action · requires oil lubrication"],
        ["Rack & pinion","Rack is a gear of infinite radius — converts rotation to linear motion\nUsed in steering systems, CNC machines, lifts, linear actuators\nBacklash must be controlled for precise positioning"],
        ["Internal gear","Driven gear has teeth on inside face · both gears rotate same direction\nAnnular gear in planetary systems · compact, coaxial drive possible\nLarger contact ratio than equivalent external pair"],
      ]}/>

      <h2 style={S.h2}>Simple vs Compound Gear Trains</h2>
      <p style={S.p}>
        A <em>simple</em> gear train has one gear per shaft. Idler gears between driver and driven change rotation direction
        but not the overall velocity ratio — only the first and last gear teeth matter for the ratio. A <em>compound</em>
        gear train places two gears on intermediate shafts, causing stage ratios to multiply and enabling very large overall
        ratios from small, practical gear sizes.
      </p>
      <DiagramSimpleCompound />
      <Table2 rows={[
        ["Simple gear train","One gear per shaft · VR = N_last / N_first (intermediate teeth cancel)\nIdler gear reverses direction without changing ratio\nLimited to modest ratios — large ratio requires impractically large gear"],
        ["Compound gear train","Two gears on each intermediate shaft, fixed together (or integral)\nVR_total = (N₂/N₁) × (N₄/N₃) × … — each stage multiplies\nAutomotive gearboxes, lathes, hoists — all use compound trains"],
        ["Reverted gear train","Input and output shafts on same axis (coaxial)\nConstraint: sum of pitch radii stage 1 = sum of pitch radii stage 2\nUsed in: clock trains, speed reducers needing compact coaxial layout"],
        ["Number of stages","Each stage typically 3:1–10:1 for spur/helical (up to 5:1 preferred)\nOverall ratio = product: 3 stages of 5:1 → 125:1\nMore stages → lower efficiency: η_total = 0.98ⁿ"],
        ["Idler gear","Changes direction of rotation between driver and last driven gear\nOdd number of idlers → same direction as driver · even number → reversed\nAlso used to bridge large centre distances"],
      ]}/>

      <h2 style={S.h2}>Epicyclic (Planetary) Gear Trains</h2>
      <p style={S.p}>
        An epicyclic gear train has one or more planet gears revolving around a central sun gear, all contained within
        a ring gear (annulus). By fixing any one of the three members (sun, ring, or carrier) and using the other two
        as input and output, a wide range of ratios — and directions — is achieved from a single compact, coaxial assembly.
      </p>
      <DiagramPlanetary />
      <Table2 rows={[
        ["Fundamental equation","N_S × ω_S + N_R × ω_R − (N_S + N_R) × ω_C = 0\n(S=sun, R=ring, C=carrier) — relates all three member speeds"],
        ["Tooth count constraint","N_R = N_S + 2×N_P  (for equally-spaced planets to mesh correctly)\nWith 3 planets: (N_S + N_R) must be divisible by 3"],
        ["Speed ratio (sun input, ring fixed)","i = 1 + N_R/N_S  → e.g. N_S=20, N_R=60: i = 1+60/20 = 4:1\nCarrier is output — widely used in automatic gearboxes"],
        ["Speed ratio (ring input, sun fixed)","i = 1 + N_S/N_R  → smaller ratio, speed increase possible"],
        ["Differential mode","If no member is fixed: two inputs → differential output\nAutomotive differential: engine input to ring, two sun outputs to wheels"],
        ["Advantages","Coaxial: input and output on same axis · very compact\nMultiple load paths through planets → high power density\nVery high ratios in small envelope; used in automatic transmissions, hub gears"],
        ["Disadvantages","More complex to manufacture and assemble · higher cost\nRequires precise tooth count relationships · bearing layout complex"],
      ]}/>

      <h2 style={S.h2}>Gear Ratio, Speed & Torque Calculations</h2>
      <DiagramRatioChart />
      <Table2 rows={[
        ["Velocity ratio (VR)","i = ω_input / ω_output = N_output / N_input  (for one pair)\nCompound: i = (N_B/N_A) × (N_D/N_C) × …\ni > 1: speed reduction (torque multiplication) · i < 1: speed increase"],
        ["Speed calculation","N_output = N_input / i\nExample: 1500 rpm input, i=20 → N_out = 75 rpm"],
        ["Torque calculation","τ_output = τ_input × i × η_total\nHigher gear ratio → proportionally more output torque (ideal)"],
        ["Power","P = τ × ω = F × v (constant along train, less η losses)\nP_in × η_total = P_out  ·  η_total = η₁ × η₂ × … × ηₙ"],
        ["Direction rule","External pair: opposite rotation · add an idler to restore direction\nEven number of external meshes: same direction as input\nOdd number of external meshes: reversed"],
        ["Algebraic sign convention","Positive = CCW by convention · negative = CW (or reversed)\ni = +N₂/N₁ (external, opposite) · i = −N₂/N₁ (internal, same)"],
      ]}/>

      <h2 style={S.h2}>Tooth Geometry & the Involute Profile</h2>
      <p style={S.p}>
        Gear teeth are shaped as <em>involutes</em> — curves generated by unwinding a string from a base circle.
        This profile guarantees a constant velocity ratio regardless of minor errors in centre distance, and concentrates
        the contact force along a fixed straight line (the line of action), simplifying bearing load calculations.
      </p>
      <DiagramToothGeometry />
      <Table2 rows={[
        ["Module m","m = d/N  (SI) — the fundamental size parameter; must match for two gears to mesh\nStandard values: 1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10, 12, 16, 20 mm"],
        ["Diametral pitch P","P = N/d  (imperial, teeth per inch) · P = 25.4/m\nHigher P → finer, smaller teeth · used in US and older British practice"],
        ["Circular pitch p","p = πd/N = πm  — arc distance between corresponding points on adjacent teeth\nAll meshing gears must have the same circular pitch (= same module)"],
        ["Pressure angle φ","Standard: 20° (BS/ISO) · legacy: 14.5°\nHigher φ: stronger teeth, larger separating force on bearings\nLower φ: quieter, more undercutting risk on small pinions"],
        ["Addendum","a = m — radial height of tooth above pitch circle"],
        ["Dedendum","b = 1.25m — radial depth of tooth below pitch circle (includes clearance)"],
        ["Clearance","c = b − a = 0.25m — gap between tip of one gear and root of meshing gear"],
        ["Contact ratio","ε = (length of path of contact) / (base pitch) · must be > 1.2 for continuous drive\nHigher contact ratio → smoother, quieter, stronger (more teeth sharing load)"],
        ["Interference (undercutting)","Occurs when pinion has too few teeth: N_min ≈ 2/(sin²φ) ≈ 17 for φ=20°\nFix: increase pressure angle, use profile shift, or use more teeth"],
        ["Profile shift","Addendum modified (shifted) to avoid undercutting or equalise strength\nPositive shift on pinion increases tooth thickness at pitch circle"],
      ]}/>

      <h2 style={S.h2}>Reverted Gear Train & Applications</h2>
      <DiagramRevertedAndApps />

      <h2 style={S.h2}>Efficiency & Power Losses</h2>
      <DiagramEfficiency />
      <Table2 rows={[
        ["Sliding friction","Dominant loss · teeth slide over each other during approach and recess phases\nOnly at pitch point is there pure rolling — friction coefficient μ ≈ 0.04–0.1 (lubricated)"],
        ["Per-mesh efficiency","Spur/helical: η ≈ 0.98–0.99 per mesh · bevel: 0.95–0.98 · worm: 0.50–0.90\nWorm self-locking condition: tan(lead angle) < μ"],
        ["Multi-stage efficiency","η_total = η₁ × η₂ × … × ηₙ\n3-stage spur train: 0.98³ ≈ 0.94 (6% lost as heat)"],
        ["Lubrication","Splash lubrication (oil bath) for moderate speeds · force-feed for high-speed gearboxes\nGear oil viscosity selected to minimise churning yet maintain film strength"],
        ["Surface finish","Ground teeth (Ra <0.4 µm) significantly reduce friction vs hobbed teeth\nRunning-in period: surfaces polish against each other, efficiency improves"],
        ["Backlash","Clearance between tooth flanks to prevent jamming due to thermal expansion\nNormal: 0.03–0.13 mm depending on module · excessive backlash → shock loads on reversal"],
      ]}/>

      <h2 style={S.h2}>Gear Train Design Procedure</h2>
      <Table2 rows={[
        ["Step 1 — Define requirements","Input speed, output speed, power, direction, duty cycle, space envelope\nDetermine acceptable number of stages (typically 2–4 for large ratios)"],
        ["Step 2 — Select gear type","Parallel shafts → spur or helical · intersecting → bevel · crossed → worm or crossed helical\nConsider noise, efficiency, self-locking requirement"],
        ["Step 3 — Allocate stage ratios","For compound train: i_stage = i_total^(1/n) (equal stages) — minimises gear sizes\nKeep any stage ratio ≤ 6:1 for spur gears to limit pinion size"],
        ["Step 4 — Choose module & tooth count","Select module from standard series based on power and speed\nMinimum teeth on pinion: N_min ≈ 17 (φ=20°) to avoid undercutting\nFrom i = N₂/N₁ and chosen N₁ → N₂ = i × N₁ (round to integer, recompute i)"],
        ["Step 5 — Calculate geometry","d = mN, centre distance C = m(N₁+N₂)/2, check fits in space envelope\nCompute addendum, dedendum, tooth thickness, clearance"],
        ["Step 6 — Check stresses","Lewis bending stress: σ = W_t / (m·b·Y)  (b=face width, Y=Lewis form factor)\nHertzian contact stress for surface durability\nFace width typically b = 8m–16m for balance of size and strength"],
        ["Step 7 — Verify speed & torque","Confirm output speed, torque, and power at each shaft\nCheck bearing loads from gear separating forces and tangential forces"],
        ["Step 8 — Lubrication & housing","Select oil viscosity and lubrication method · design housing for thermal management\nCheck operating temperature: power loss × thermal resistance < ΔT_max"],
      ]}/>

      <h2 style={S.h2}>Failure Modes & Maintenance</h2>
      <Table2 rows={[
        ["Pitting (surface fatigue)","Small craters on tooth flank from repeated Hertzian contact stress exceeding endurance limit\nProgressive pitting → destructive pitting → tooth breakage · fix: better surface finish, harder material"],
        ["Bending fatigue","Cracks at tooth root fillet from cyclic bending stress → eventual fracture\nMost catastrophic failure mode · fix: increase module, face width, or material grade"],
        ["Scuffing (scoring)","Severe adhesive wear from breakdown of oil film at high load/speed\nInstantaneous welding and tearing of tooth flanks · fix: EP (extreme pressure) additives in oil"],
        ["Abrasive wear","Fine particles in oil gradually remove material · reduces tooth accuracy and backlash\nFix: oil filtration, sealed housing, regular oil changes"],
        ["Overload fracture","Single-event tooth fracture from shock load exceeding ultimate strength\nFix: shock absorbers, torque limiters, select appropriate material and module"],
        ["Noise & vibration","Gear whine from tooth errors, misalignment, or resonance · measured in transmission error\nFix: improve tooth accuracy grade (ISO 3–8), correct alignment, add damping"],
        ["Inspection intervals","Check backlash, surface condition, oil level/condition every 500–2000 h\nVibration analysis (FFT) detects developing faults early — gear mesh frequency = N × rpm/60"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Gear ratio: i = N₂/N₁ = ω₁/ω₂ = d₂/d₁",
        "Module: m = d/N = p/π  (p = circular pitch) — same for both gears in a mesh",
        "Pitch diameter: d = m·N",
        "Centre distance: C = m(N₁+N₂)/2  (external)  ·  C = m(N₂−N₁)/2  (internal)",
        "Addendum: a = m  ·  Dedendum: b = 1.25m  ·  Whole depth: h = 2.25m",
        "Compound VR: i = (N₂/N₁)×(N₄/N₃)×…  (product of all stage ratios)",
        "Planetary (sun in, ring fixed, carrier out): i = 1 + N_R/N_S",
        "Planetary fundamental: N_S·ω_S + N_R·ω_R − (N_S+N_R)·ω_C = 0",
        "Lewis bending stress: σ = W_t / (m·b·Y)  (W_t = tangential force, b = face width, Y = form factor)",
        "Tangential force: W_t = 2τ/d = P/(ω·r)",
        "Efficiency: η_total = η₁×η₂×…×ηₙ  ·  Spur ≈ 0.98–0.99 per mesh",
        "Torque: τ_out = τ_in × i × η_total  ·  Speed: ω_out = ω_in / i",
      ]}/>
    </div>
  );
}